import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { onStaleWrite, type Event2Label } from "@wedding/shared";

interface WeddingConfig {
  language?: string;
  registration?: {
    allowSelfRegister?: boolean;
    requireOtp?: boolean;
    registrationOpensAt?: string | null;
    registrationClosesAt?: string | null;
  };
  [key: string]: unknown;
}

interface WeddingInfo {
  slug: string;
  title: string;
  date_time: string | null;
  location: string | null;
  description: string | null;
  /** Ana etkinliğin türü — null ise UI "Düğün" yazar (migration 051). */
  event1_label: Event2Label | null;
  event1_label_custom: string | null;
  /** Opsiyonel ikinci etkinlik — ana tarihten önce de sonra da olabilir. */
  event2_date_time: string | null;
  event2_location: string | null;
  event2_label: Event2Label | null;
  event2_label_custom: string | null;
  bg_auth: string | null;
  bg_countdown: string | null;
  bg_rsvp: string | null;
  bg_auth_key: string | null;
  bg_countdown_key: string | null;
  bg_rsvp_key: string | null;
  config: WeddingConfig;
  // Join-theme media (resolved to CDN URLs by /api/admin/weddings/:slug) —
  // consumed by the shared join-theme editor.
  cover_photo_url: string | null;
  join_cover_images: string[];
  join_frame_count: number;
}

interface WeddingContextValue {
  wedding: WeddingInfo | null;
  isLoading: boolean;
  error: string | null;
  slug: string;
  refresh: () => void;
}

const WeddingContext = createContext<WeddingContextValue | null>(null);

export function WeddingProvider({ children }: { children: React.ReactNode }) {
  const { slug } = useParams<{ slug: string }>();
  const [wedding, setWedding] = useState<WeddingInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Kayıt başka yerden değiştiği için reddedildiyse (409 stale_write) düğün verisi tazelenir:
  // form güncel değeri gösterir, bir sonraki kayıt güncel sürümle gider.
  useEffect(() => onStaleWrite(refresh), [refresh]);

  // Track slug separately so we only clear stale wedding data on slug change,
  // not on background refreshes (refreshKey changes).
  const prevSlugRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (prevSlugRef.current !== slug) {
      setWedding(null);
      prevSlugRef.current = slug;
    }
    setError(null);
    setIsLoading(true);

    if (!slug) {
      setError("Wedding not found");
      setIsLoading(false);
      return;
    }

    // Use admin endpoint so archived weddings are accessible to staff
    const base = `/api/admin/weddings/${slug}`;

    fetch(base, { credentials: "include" })
      .then((res) => res.json())
      .then((data: { ok: boolean; data?: WeddingInfo; error?: string }) => {
        if (data.ok && data.data) {
          setWedding({ ...data.data, slug });
        } else {
          setError(data.error ?? "Wedding not found");
        }
      })
      .catch(() => setError("Failed to load wedding"))
      .finally(() => setIsLoading(false));
  }, [slug, refreshKey]);

  return (
    <WeddingContext.Provider value={{ wedding, isLoading, error, slug: slug ?? "", refresh }}>
      {children}
    </WeddingContext.Provider>
  );
}

export function useWedding() {
  const ctx = useContext(WeddingContext);
  if (!ctx) throw new Error("useWedding must be used within WeddingProvider");
  return ctx;
}
